import moment from 'moment'
import { get, post } from '../baseService'
import { SERVICE_DATE_FORMAT } from 'app/constants/date'

import { AlertReminderConfigurationModel } from 'app/models/configuration/alertReminderConfigurationModel'
import { BasicConfigurationModel } from 'app/models/configuration/basicConfigurationModel'
import { BusinessTripConfigurationModel } from 'app/models/configuration/businessTripConfigurationModel'
import { BusinessTripRatesConfigurationModel } from 'app/models/configuration/businessTripRatesConfigurationModel'
import { CountryConfigurationModel } from 'app/models/configuration/countryConfigurationModel'
import { GpsConfigurationModel } from 'app/models/configuration/gpsConfigurationModel'
import { PrintoutConfigurationModel } from 'app/models/configuration/printoutConfigurationModel'
import { TimesheetAdditionalConfigurationModel } from 'app/models/configuration/timesheetAdditionalConfigurationModel'
import { TimesheetConfigurationModel } from 'app/models/configuration/timesheetConfigurationModel'
import { ViolationConfigurationModel } from 'app/models/configuration/violationConfigurationModel'
import { MinimumWageConfigurationModel } from 'app/models/configuration/minimumWageConfigurationModel'
import { ConfigurationType } from 'app/constants/configuration/configurationType'
import { IntegrationConfigurationModel } from 'app/models/configuration/integrationConfigurationModel'

const create = (): any => {
  const getViolationConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/violations`)

  const saveViolationConfiguration = (
    companyId: string,
    configuration: ViolationConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveViolationsConfiguration`,
      configuration,
      handleErrorInternally,
    )

  const saveBusinessTripConfiguration = (
    companyId: string,
    configuration: BusinessTripConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveBusinessTripConfiguration`,
      configuration,
      handleErrorInternally,
    )

  const saveBusinessTripRatesConfiguration = (
    companyId: string,
    configuration: BusinessTripRatesConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveBusinessTripRatesConfiguration`,
      configuration,
      handleErrorInternally,
    )

  const saveAlertRemindersConfiguration = (
    companyId: string,
    configuration: AlertReminderConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveAlertRemindersConfiguration`,
      configuration,
      handleErrorInternally,
    )

  const savePrintoutConfiguration = (
    companyId: string,
    configuration: PrintoutConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/savePrintoutConfiguration`,
      configuration,
      handleErrorInternally,
    )

  const saveGpsConfiguration = (
    companyId: string,
    configuration: GpsConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveGpsConfiguration`,
      { configuration: configuration },
      handleErrorInternally,
    )

  const saveIntegrationConfiguration = (
    companyId: string,
    configuration: IntegrationConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveIntegrationConfiguration`,
      { configuration: configuration },
      handleErrorInternally,
    )

  const saveCountryConfiguration = (
    companyId: string,
    configuration: Array<CountryConfigurationModel>,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveCountryConfiguration`,
      { configuration: configuration },
      handleErrorInternally,
    )

  const saveTimesheetConfiguration = (
    companyId: string,
    configuration: TimesheetConfigurationModel,
  ) =>
    post(
      `api/${companyId}/configuration/saveTimesheetConfiguration`,
      { configuration: configuration },
      false,
    )

  const saveMinimumWageConfiguration = (
    companyId: string,
    configuration: MinimumWageConfigurationModel,
  ) =>
    post(
      `api/${companyId}/configuration/saveMinimumWageConfiguration`,
      { configuration: configuration },
      false,
    )

  const restoreDefaultConfiguration = (
    companyId: string,
    configurationType: ConfigurationType,
    country?: string,
  ) =>
    post(
      `api/${companyId}/configuration/restoreDefaultConfiguration`,
      { configurationType, country },
      false,
    )

  const saveTimesheetAdditionalConfiguration = (
    companyId: string,
    configuration: TimesheetAdditionalConfigurationModel,
  ) =>
    post(
      `api/${companyId}/configuration/saveTimesheetAdditionalConfiguration`,
      { configuration: configuration },
      true,
    )

  const restoreBusinessTripRatesConfiguration = (
    companyId: string,
    restoreOption: number,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/restoreBusinessTripRatesConfiguration`,
      { restoreAllowanceOption: restoreOption },
      handleErrorInternally,
    )

  const getBusinessTripConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/businessTrips`)

  const getBusinessTripRatesConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/businessTripRates`)

  const getAlertRemindersConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/alertReminders`)

  const getPrintoutConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/printout`)

  const getGpsConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/gps`)

  const getIntegrationConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/integration`)

  const getMinimumWageConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/minimumWage`)

  const getExchangeRateConfiguration = (
    companyId: string,
    rateDate: any,
    source: number,
  ) =>
    get(
      `api/${companyId}/configuration/exchangeRates?&rateDate=${moment(rateDate).format(SERVICE_DATE_FORMAT)}&sourceType=${source}`,
    )

  const downloadExchangeRates = (
    companyId: string,
    dateFrom: any,
    dateTo: any,
    source: number,
    handleErrorInternally: boolean = false,
  ) =>
    post(
      `api/${companyId}/configuration/downloadExchangeRates`,
      {
        dateFrom: moment(dateFrom).format(SERVICE_DATE_FORMAT),
        dateTo: moment(dateTo).format(SERVICE_DATE_FORMAT),
        sourceType: source,
      },
      handleErrorInternally,
    )

  const getCountryConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/country`)

  const getTimesheetConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/timesheet`)

  const getTimesheetAdditionalConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/timesheetAdditional`)

  const getBasicConfiguration = (companyId: string) =>
    get(`api/${companyId}/configuration/basic`)

  const saveBasicConfiguration = (
    companyId: string,
    basic: BasicConfigurationModel,
    handleErrorInternally: boolean = true,
  ) =>
    post(
      `api/${companyId}/configuration/saveBasicConfiguration`,
      basic,
      handleErrorInternally,
    )

  return {
    getViolationConfiguration,
    getBusinessTripConfiguration,
    getBusinessTripRatesConfiguration,
    getTimesheetConfiguration,
    getTimesheetAdditionalConfiguration,
    getBasicConfiguration,
    getCountryConfiguration,
    saveViolationConfiguration,
    saveBasicConfiguration,
    saveBusinessTripConfiguration,
    saveBusinessTripRatesConfiguration,
    restoreBusinessTripRatesConfiguration,
    saveCountryConfiguration,
    saveTimesheetConfiguration,
    saveTimesheetAdditionalConfiguration,
    getAlertRemindersConfiguration,
    saveAlertRemindersConfiguration,
    getPrintoutConfiguration,
    savePrintoutConfiguration,
    getGpsConfiguration,
    saveGpsConfiguration,
    getIntegrationConfiguration,
    saveIntegrationConfiguration,
    getExchangeRateConfiguration,
    downloadExchangeRates,
    getMinimumWageConfiguration,
    saveMinimumWageConfiguration,
    restoreDefaultConfiguration,
  }
}

export default {
  create,
}
